/**
 * Community Audit Metrics Chart Component
 *
 * Displays a multi-line chart showing accuracy, precision, recall, and f1 score over time.
 * Handles data processing, SVG path generation, and loading states.
 */

import type { AuditReport } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { Shimmer } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { AUDIT_METRIC_COLORS } from '../shared/constants/audit-metric-colors';

import { ChartHeader } from './CommunityAuditAccuracyChart/ChartHeader';
import { MetricsChart } from './CommunityAuditAccuracyChart/MetricsChart';
import * as styles from './CommunityAuditAccuracyChart.css';
import * as shimmerStyles from './shared/shimmer.css';

interface MetricsDataPoint {
  date: string;
  accuracy: number; // 0-100
  precision: number; // 0-100
  recall: number; // 0-100
  f1Score: number; // 0-100
}

interface CommunityAuditAccuracyChartProps {
  auditSessions: FlexibleAuditSession[];
  auditReports?: AuditReport[]; // Optional audit reports for full metrics
  isLoading?: boolean;
}

export function CommunityAuditAccuracyChart({ auditSessions, auditReports = [], isLoading = false }: CommunityAuditAccuracyChartProps) {
  // Generate metrics data from audit reports (preferred) or fallback to audit sessions (accuracy only)
  const metricsData: MetricsDataPoint[] = useMemo(() => {
    if (isLoading) return [];

    // If we have audit reports with metrics, use those for full multi-line chart
    if (auditReports.length > 0) {
      const reportsWithMetrics = auditReports
        .filter(report => report.metrics?.overall) // Accept any report with metrics, regardless of status
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      if (reportsWithMetrics.length > 0) {
        return reportsWithMetrics
          .map(report => {
            const metrics = report.metrics?.overall;

            if (!metrics) return null;

            return {
              date: report.createdAt,
              accuracy: Math.round(metrics.accuracy * 100),
              precision: Math.round(metrics.precision * 100),
              recall: Math.round(metrics.recall * 100),
              f1Score: Math.round(metrics.f1Score * 100),
            };
          })
          .filter((item): item is MetricsDataPoint => item !== null);
      }
    }

    // Check for audit sessions with accuracy data
    const completedSessions = auditSessions
      .filter(
        (session): session is FlexibleAuditSession & { accuracy: number; completedAt: string } =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          typeof (session as any).accuracy === 'number' && typeof session.completedAt === 'string'
      )
      .sort((a, b) => new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime());

    if (completedSessions.length > 0) {
      return completedSessions.map(session => ({
        date: session.completedAt,
        accuracy: session.accuracy,
        precision: session.accuracy, // Fallback: use accuracy as approximation
        recall: session.accuracy, // Fallback: use accuracy as approximation
        f1Score: session.accuracy, // Fallback: use accuracy as approximation
      }));
    }

    // If no real data available, return empty array (no mock data)
    return [];
  }, [auditSessions, auditReports, isLoading]);

  // Generate SVG paths for multi-line chart
  const chartData = useMemo(() => {
    if (isLoading || metricsData.length < 1)
      return {
        accuracy: { pathData: '', points: [] },
        precision: { pathData: '', points: [] },
        recall: { pathData: '', points: [] },
        f1Score: { pathData: '', points: [] },
      };

    const width = 300; // SVG width
    const height = 200; // SVG height
    const padding = 16;

    // Find global min/max across all metrics for consistent scaling
    const allValues = metricsData.flatMap(d => [d.accuracy, d.precision, d.recall, d.f1Score]);
    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);
    const range = maxValue - minValue || 1;

    // Generate paths for each metric
    const metrics = ['accuracy', 'precision', 'recall', 'f1Score'] as const;
    const result = {} as Record<(typeof metrics)[number], { pathData: string; points: Array<{ x: number; y: number; value: number }> }>;

    metrics.forEach(metric => {
      const points = metricsData.map((point, index) => {
        // For single data point, center it horizontally
        const x = metricsData.length === 1 ? width / 2 : padding + (index / (metricsData.length - 1)) * (width - 2 * padding);
        const y = height - padding - ((point[metric] - minValue) / range) * (height - 2 * padding);

        return { x, y, value: point[metric] };
      });

      // For single data point, create a small circle instead of a line
      const pathData =
        metricsData.length === 1
          ? `M ${points[0].x - 2} ${points[0].y} A 2 2 0 1 1 ${points[0].x + 2} ${points[0].y} A 2 2 0 1 1 ${points[0].x - 2} ${points[0].y}`
          : points.reduce((path, point, index) => {
              if (index === 0) {
                return `M ${point.x} ${point.y}`;
              }

              return `${path} L ${point.x} ${point.y}`;
            }, '');

      result[metric] = { pathData, points };
    });

    return result;
  }, [metricsData, isLoading]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.graphPanel}>
        <div className={styles.graphHeader}>
          <Shimmer className={shimmerStyles.shimmerTitle} />
          <Shimmer className={shimmerStyles.shimmerValue} />
        </div>
        <div className={styles.sparklineContainer}>
          <Shimmer className={shimmerStyles.shimmerChart} />
        </div>
        <div className={styles.timeLabels}>
          <Shimmer className={shimmerStyles.shimmerTimeLabel} />
          <Shimmer className={shimmerStyles.shimmerTimeValue} />
        </div>
        {/* Legend shimmer */}
        <div className={styles.chartLegend}>
          <Shimmer className={shimmerStyles.shimmerLabel} />
          <Shimmer className={shimmerStyles.shimmerLabel} />
          <Shimmer className={shimmerStyles.shimmerLabel} />
          <Shimmer className={shimmerStyles.shimmerLabel} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.graphPanel}>
      <ChartHeader />
      <MetricsChart chartData={chartData} isLoading={isLoading} />
      {/* Time labels below the panel */}
      <div className={styles.timeLabels}>
        <span>1 Year Ago</span>
        <span>Now</span>
      </div>
      {/* Legend below time labels using shared constants */}
      <div className={styles.chartLegend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.accuracy }} />
          <span className={styles.legendLabel}>Accuracy</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.precision }} />
          <span className={styles.legendLabel}>Precision</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.recall }} />
          <span className={styles.legendLabel}>Recall</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.f1Score }} />
          <span className={styles.legendLabel}>F1 Score</span>
        </div>
      </div>
    </div>
  );
}
